import React from 'react';
import type {
  DefaultValues,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { Box } from '@mui/material';

export interface FormProps<T extends FieldValues> {
  defaultValues: DefaultValues<T>;
  onSubmit: (data: T, methods: UseFormReturn<T>) => Promise<void> | void;
  resetOn?: unknown;
  containerComponent?: 'form' | 'div';
  id?: string;
  children: React.ReactNode | ((methods: UseFormReturn<T>) => React.ReactNode);
}

const Form = <T extends FieldValues>({
  defaultValues,
  onSubmit,
  resetOn,
  containerComponent = 'form',
  id,
  children,
}: FormProps<T>) => {
  const methods = useForm<T>({ defaultValues });
  const { handleSubmit, reset } = methods;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  React.useEffect(() => {
    if (resetOn !== undefined) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, resetOn]);

  const handleFormSubmit = async (data: T) => {
    await onSubmit(data, methods);
  };

  return (
    <FormProvider {...methods}>
      <Box
        id={id}
        component={containerComponent}
        noValidate={containerComponent === 'form' ? true : undefined}
        onSubmit={
          containerComponent === 'form'
            ? (event: React.FormEvent<HTMLFormElement>) => {
                event.stopPropagation();
                void handleSubmit(handleFormSubmit)(event);
              }
            : undefined
        }
      >
        {typeof children === 'function' ? children(methods) : children}
      </Box>
    </FormProvider>
  );
};

export default Form;
